import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export interface TourStep {
  id: string;
  target: string; // CSS selector or data-tour attribute
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  route?: string; // Route to navigate to before showing this step
  action?: () => void; // Action to perform before showing step
}

export interface Tour {
  id: string;
  name: string;
  description: string;
  steps: TourStep[];
}

interface TourContextType {
  currentTour: Tour | null;
  currentStepIndex: number;
  isActive: boolean;
  startTour: (tour: Tour) => void;
  nextStep: () => void;
  previousStep: () => void;
  endTour: () => void;
  skipTour: () => void;
  hasCompletedTour: (tourId: string) => boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTour, setCurrentTour] = useState<Tour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const location = useLocation();

  const hasCompletedTour = useCallback((tourId: string): boolean => {
    const completed = localStorage.getItem(`tour_completed_${tourId}`);
    return completed === 'true';
  }, []);

  const startTour = useCallback((tour: Tour) => {
    setCurrentTour(tour);
    setCurrentStepIndex(0);
  }, []);

  const nextStep = useCallback(() => {
    if (!currentTour) return;
    
    if (currentStepIndex < currentTour.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Tour complete
      localStorage.setItem(`tour_completed_${currentTour.id}`, 'true');
      setCurrentTour(null);
      setCurrentStepIndex(-1);
    }
  }, [currentTour, currentStepIndex]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);

  const endTour = useCallback(() => {
    if (currentTour) {
      localStorage.setItem(`tour_completed_${currentTour.id}`, 'true');
    }
    setCurrentTour(null);
    setCurrentStepIndex(-1);
  }, [currentTour]);

  const skipTour = useCallback(() => {
    setCurrentTour(null);
    setCurrentStepIndex(-1);
  }, []);

  // Handle route changes for tour steps
  useEffect(() => {
    if (currentTour && currentStepIndex >= 0) {
      const step = currentTour.steps[currentStepIndex];
      if (step.route && location.pathname !== step.route) {
        // Wait a bit for navigation, then continue
        setTimeout(() => {
          // Step will be shown by TourOverlay after navigation
        }, 500);
      }
    }
  }, [location.pathname, currentTour, currentStepIndex]);

  return (
    <TourContext.Provider
      value={{
        currentTour,
        currentStepIndex,
        isActive: currentTour !== null && currentStepIndex >= 0,
        startTour,
        nextStep,
        previousStep,
        endTour,
        skipTour,
        hasCompletedTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

